import React from "react";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import Reveal from "../components/Reveal";
import SVGMap from "../components/SVGMap";
import { WhatsAppCTA } from "../components/Buttons";
import { PhoneIconBtn, MailIconBtn } from "../components/ContactIcons";
import { SITE } from "../lib/site";
import useSEO from "../hooks/useSEO";

export default function Contact() {
  const { lat, lng } = SITE.coordinates;
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  useSEO({
    title: "Contact SVMD & Associates — Coimbatore CA Firm",
    description:
      "Call +91 93447 33377 or visit our Mettupalayam Road, Coimbatore office. WhatsApp, email and directions for SVMD & Associates Chartered Accountants.",
    keywords:
      "SVMD contact, CA firm Coimbatore phone, Coimbatore Chartered Accountant, Mettupalayam Road CA office",
    canonical: "/contact",
  });

  return (
    <>
      {/* HERO (DARK) */}
      <section className="relative bg-ink text-paper overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-100 animate-grid-drift" aria-hidden />
        <div className="absolute inset-0 bg-glow-radial" aria-hidden />
        <div className="absolute -right-40 top-10 w-[700px] h-[700px] rounded-full blur-3xl opacity-40 bg-[radial-gradient(circle,rgba(37,99,235,0.5)_0%,transparent_60%)]" aria-hidden />

        <div className="relative max-w-site mx-auto px-6 md:px-12 lg:px-16 pt-28 md:pt-36 pb-20 md:pb-24">
          <Reveal>
            <div className="eyebrow eyebrow-voltage mb-6">Contact</div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-display font-semibold text-[40px] sm:text-[56px] md:text-[72px] lg:text-[80px] leading-[1.04] tracking-[-0.03em] max-w-5xl">
              Whatever you need done&mdash;<br className="hidden md:block" />
              <span className="text-voltage">reach out.</span>
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-7 max-w-2xl text-paper/75 text-base md:text-[18px] leading-[1.55]">
              Compliance, an audit, or a specific issue. Share your
              requirement and we&rsquo;ll take it forward.
            </p>
          </Reveal>
          <Reveal delay={280}>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <WhatsAppCTA testId="contact-hero-whatsapp-cta" />
              <PhoneIconBtn variant="dark" testId="contact-hero-phone-icon" />
              <MailIconBtn variant="dark" testId="contact-hero-email-icon" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* DETAILS + MAP (LIGHT, with embedded dark SVG map) */}
      <section className="bg-paper text-ink">
        <div className="max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-24">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            {/* Details */}
            <div className="col-span-12 md:col-span-5 space-y-6">
              <Reveal>
                <div className="eyebrow mb-3">Office</div>
                <div data-testid="contact-address" className="font-display text-[24px] md:text-[28px] leading-[1.18] tracking-tight">
                  {SITE.name}
                </div>
                <div className="mt-2 text-slate-muted text-[15px] leading-relaxed">
                  {SITE.address.line1}<br />
                  {SITE.address.line2}<br />
                  {SITE.address.line3}
                </div>
                <a
                  href={directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-directions"
                  className="mt-4 inline-flex items-center gap-2 text-electric text-[13px] font-medium link-wipe"
                >
                  Get directions <ArrowUpRight size={14} strokeWidth={2} />
                </a>
              </Reveal>

              <Reveal delay={100}>
                <div className="border-t border-[var(--border-light)] pt-5">
                  <div className="flex items-center gap-2 eyebrow mb-2">
                    <Phone size={14} strokeWidth={1.6} /> Phone
                  </div>
                  <div
                    data-testid="contact-phone"
                    className="font-display text-[22px] md:text-[28px] leading-tight select-all"
                  >
                    {SITE.phoneDisplay}
                  </div>
                </div>
              </Reveal>

              <Reveal delay={150}>
                <div className="border-t border-[var(--border-light)] pt-5">
                  <div className="flex items-center gap-2 eyebrow mb-2">
                    <Mail size={14} strokeWidth={1.6} /> Email
                  </div>
                  <div
                    data-testid="contact-email"
                    className="font-display text-[22px] md:text-[28px] leading-tight select-all break-all"
                  >
                    {SITE.email}
                  </div>
                </div>
              </Reveal>

              <Reveal delay={200}>
                <div className="border-t border-[var(--border-light)] pt-5 flex items-center gap-2 eyebrow">
                  <MapPin size={14} strokeWidth={1.6} />
                  <span className="font-mono normal-case tracking-wider">
                    {lat}° N · {lng}° E
                  </span>
                </div>
              </Reveal>
            </div>

            {/* Map (custom SVG, on-brand) */}
            <Reveal delay={120} className="col-span-12 md:col-span-7">
              <div className="rounded-xl overflow-hidden border border-[var(--border-light)] aspect-[4/3] md:aspect-auto md:h-[560px]">
                <SVGMap className="w-full h-full" />
              </div>
              <div className="mt-3 flex items-center justify-between text-[11px] text-ink-tertiary">
                <span className="font-mono tracking-wider">SCHEMATIC · NOT TO SCALE</span>
                <a
                  href={directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-map-link"
                  className="text-electric hover:text-ink-700 link-wipe"
                >
                  Open in Maps
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
